"use client";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives/Popover/Popover";
import type { ITeam } from "@gettactic/api";
import { Users } from "lucide-react";
import React, { useState } from "react";
import { useTeams } from "@/features/teams/hooks/useTeams";

export interface TeamPickerProps {
  /** Element that opens the picker; rendered via Radix `asChild`. */
  trigger: React.ReactNode;
  onSelect: (team: ITeam) => void;
  /** Teams already picked; hidden from the results. */
  excludeTeamIds?: string[];
  placeholder?: string;
  emptyLabel?: string;
  align?: "start" | "center" | "end";
}

/**
 * Team picker in the shape of the people picker: the directory teams,
 * searched as you type, one row per team with its size.
 */
export const TeamPicker: React.FC<TeamPickerProps> = ({
  trigger,
  onSelect,
  excludeTeamIds = [],
  placeholder = "Search teams",
  emptyLabel = "No teams found",
  align = "end"
}) => {
  const searchInputId = React.useId();
  const [isOpen, setIsOpen] = useState(false);
  // SearchInput debounces internally, so this already is the debounced value.
  const [query, setQuery] = useState("");
  const { teamList, isFetching } = useTeams({
    type: "directory",
    hidden: false
  });

  const excluded = new Set(excludeTeamIds);
  const teams = ((teamList?.elements ?? []) as ITeam[]).filter(
    (team) => !excluded.has(team.id)
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setQuery("");
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} showArrow={false} className="w-80 p-0">
        <div className="border-neutral-100 border-b p-2">
          <SearchInput
            inputId={searchInputId}
            query={query}
            setQuery={setQuery}
            placeholder={placeholder}
            fullWidth
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {teams.map((team) => (
            <button
              key={team.id}
              type="button"
              onClick={() => {
                onSelect(team);
                setIsOpen(false);
                setQuery("");
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-neutral-50"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-primary">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900 text-sm">
                  {team.name}
                </div>
                <div className="truncate text-neutral-500 text-xs">
                  {team.total_users}{" "}
                  {team.total_users === 1 ? "member" : "members"}
                </div>
              </div>
            </button>
          ))}
          {teams.length === 0 && !isFetching ? (
            <p className="px-3 py-6 text-center text-neutral-400 text-sm">
              {emptyLabel}
            </p>
          ) : null}
          {isFetching && teams.length === 0 ? (
            <p className="px-3 py-3 text-center text-neutral-400 text-xs">
              Loading…
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
